/**
 * useUserProfiles - 批次取得使用者暱稱/頭像 + module-level 快取
 * 同一個 session 內已經查過的 userId 不會重複呼叫 API。
 */

import { useEffect, useState } from 'react';
import { getUserProfiles } from '../services/userProfileService';
import type { UserProfile } from '../types/userProfile';

const userProfileCache = new Map<number, UserProfile>();

function buildMapFromCache(userIds: number[]): Map<number, UserProfile> {
  const map = new Map<number, UserProfile>();
  userIds.forEach((id) => {
    const cached = userProfileCache.get(id);
    if (cached) map.set(id, cached);
  });
  return map;
}

/**
 * 查無資料時的預設頭像文字（暱稱或帳號皆拿不到時使用）
 */
export function getAvatarFallback(nickname: string | undefined): string {
  return nickname?.trim()?.charAt(0)?.toUpperCase() || '?';
}

export function useUserProfiles(userIds: number[]) {
  const idsKey = Array.from(new Set(userIds)).sort((a, b) => a - b).join(',');
  const [profiles, setProfiles] = useState<Map<number, UserProfile>>(() => buildMapFromCache(userIds));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',').map(Number) : [];

    if (ids.length === 0) {
      setProfiles(new Map());
      return;
    }

    const missingIds = ids.filter((id) => !userProfileCache.has(id));

    if (missingIds.length === 0) {
      setProfiles(buildMapFromCache(ids));
      return;
    }

    let cancelled = false;
    setLoading(true);

    getUserProfiles(missingIds)
      .then((fetched) => {
        if (cancelled) return;
        fetched.forEach((profile) => userProfileCache.set(profile.userId, profile));
        setProfiles(buildMapFromCache(ids));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return { profiles, loading };
}
